import React from 'react';
import Tooltip from '@/components/Tooltip';

// Define a type for the segment
interface PieSegment {
  path: string;
  fill: string;
  labelX: number;
  labelY: number;
  labelAngle: number;
  label: string;
  multiLineLabel?: string[]; // Add support for multi-line labels
  fillOpacity: number;
}

export default function ComputeAllocation2024v2027() {
  // Chart dimensions
  const width = 1000;
  const height = 500;
  const margin = { top: 0, right: 240, bottom: 0, left: 100 };

  // Common values
  const centerY = height / 2;

  // 2024 chart positioning (left side) - smaller and more to the left
  const centerX2024 = width / 4.8;
  const radius2024 = Math.min(width / 2, height) / 6;
  const innerRadius2024 = radius2024 * 0.45;

  // 2027 chart positioning (right side)
  const centerX2027 = (width * 2.6) / 4;
  const radius2027 = Math.min(width / 2, height) / 3;
  const innerRadius2027 = radius2027 * 0.45;

  // Data for the 2024 pie chart
  const data2024 = [
    { label: "研究実験", value: 4, tooltip: "Research experiments"},
    { label: " ", value: 1, tooltip: "Running AI assistants internally"},
    { label: "学習", value: 40, tooltip: "Training"},
    { label: "データ生成", value: 20 },
    { label: "外部展開", value: 35 },
  ];

  // Data for the 2027 pie chart
  const data2027 = [
    { label: "研究実験", value: 28 },
    { label: "AIアシスタント\n運用", value: 6 }, // Split with newline
    { label: "学習", value: 24 },
    { label: "データ生成", value: 22 },
    { label: "外部展開", value: 20 }
  ];

  // Create pie chart paths function
  const generatePieSegments = (
    data: Array<{label: string, value: number}>,
    centerX: number,
    chartRadius: number,
    chartInnerRadius: number
  ) => {
    let currentAngle = 0;
    const segments: PieSegment[] = [];

    // Calculate total for percentage calculations
    const total = data.reduce((sum, item) => sum + item.value, 0);

    // Sort data by value in descending order
    const sortedData = data//[...data].sort((a, b) => b.value - a.value);

    sortedData.forEach((item, index) => {
      // Calculate segment angles
      const angleSize = (item.value / total) * 2 * Math.PI;
      const startAngle = currentAngle - Math.PI / 2; // Start from the top (- PI/2)
      const endAngle = startAngle + angleSize;

      // Calculate outer arc coordinates
      const startX = centerX + Math.cos(startAngle) * chartRadius;
      const startY = centerY + Math.sin(startAngle) * chartRadius;
      const endX = centerX + Math.cos(endAngle) * chartRadius;
      const endY = centerY + Math.sin(endAngle) * chartRadius;

      // Calculate inner arc coordinates
      const innerStartX = centerX + Math.cos(startAngle) * chartInnerRadius;
      const innerStartY = centerY + Math.sin(startAngle) * chartInnerRadius;
      const innerEndX = centerX + Math.cos(endAngle) * chartInnerRadius;
      const innerEndY = centerY + Math.sin(endAngle) * chartInnerRadius;

      // Create path - move to outer start, arc to outer end, line to inner end, arc back to inner start, close
      const largeArcFlag = angleSize > Math.PI ? 1 : 0;
      const path = [
        `M ${startX} ${startY}`,
        `A ${chartRadius} ${chartRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        `L ${innerEndX} ${innerEndY}`,
        `A ${chartInnerRadius} ${chartInnerRadius} 0 ${largeArcFlag} 0 ${innerStartX} ${innerStartY}`,
        'Z'
      ].join(' ');

      // Calculate label position
      // Position labels slightly outside the segments
      const labelAngle = startAngle + (angleSize / 2);

      // Slightly different label positioning based on position in the circle
      let labelRadius = chartRadius * 1.18; // Default label distance

      const labelX = centerX + Math.cos(labelAngle) * labelRadius;
      const labelY = centerY + Math.sin(labelAngle) * labelRadius;

      // Determine fill color based on size ranking
      let fill = "#333333"; // Default gray
      let fillOpacity = 0.9;

      if (item.label === "研究実験") {
        // Largest slice - full accent color
        fill = "var(--accent)";
        fillOpacity = 0.9;
      } else if (item.label === "AIアシスタント\n運用" || item.label === " ") {
        // Second largest slice - accent color with lower opacity
        fill = "var(--accent)";
        fillOpacity = 0.6;
      } else {
        // Remaining slices - varying shades of gray
        const offset = 2;
        const grayValue = 20+(index-offset) * 80;
        fill = `rgb(${grayValue}, ${grayValue}, ${grayValue})`;
        fillOpacity = 0.9;
      }

      // Process multi-line labels
      const multiLineLabel = item.label.split('\n');

      segments.push({
        path,
        fill,
        labelX,
        labelY,
        labelAngle,
        label: item.label,
        multiLineLabel: multiLineLabel.length > 1 ? multiLineLabel : undefined,
        fillOpacity
      });

      currentAngle += angleSize;
    });

    return segments;
  };

  // Generate segments for both charts with their respective radii
  const segments2024 = generatePieSegments(data2024, centerX2024, radius2024, innerRadius2024);
  const segments2027 = generatePieSegments(data2027, centerX2027, radius2027, innerRadius2027);

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title - using the same style as before */}
        <text
          x={0}
          y={40}
          fontSize="28"
          fontFamily="monospace"
          className="text-[28px] font-mono md:text-[24px] sm:text-[20px]"
          fill="#333333"
        >
          OpenBrainの計算資源配分：2024年と2027年
        </text>
        <text
            x={centerX2024}
            y={centerY-5 +28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="20"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            2024
          </text>

          <text
            x={centerX2027}
            y={centerY-4+28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="30"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            2027
          </text>
          <text
            x={centerX2024}
            y={centerY+12+28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="12"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            推定
          </text>

          <text
            x={centerX2027}
            y={centerY+20+28}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="14"
            fontFamily="monospace"
            fontWeight="bold"
            fill="#333333"
          >
            予測
          </text>

        <g transform={`translate(0, 30)`}>
          {/* 2024 Pie segments */}
          {segments2024.map((segment, i) => (
            <g key={`segment-2024-${i}`}>
                  <path
                    d={segment.path}
                    fill={segment.fill}
                    fillOpacity={segment.fill === "var(--accent)" ? segment.fillOpacity : 0.9}
                    stroke="#ffffff"
                    strokeWidth=".5"
                  />
            </g>
          ))}

          {/* 2024 Labels */}
          {segments2024.map((segment, i) => {
            // Determine text anchor based on position
            const textAnchor = (segment.label === "研究実験") ? "middle" : ((segment.label === "学習") ? "start" : "end");

            // If it's a multi-line label
            if (segment.multiLineLabel && segment.multiLineLabel.length > 1) {
              return (
                <g key={`label-2024-${i}`}>
                  {segment.multiLineLabel.map((line, lineIndex) => (
                    <text
                      key={`label-2024-${i}-line-${lineIndex}`}
                      x={segment.labelX}
                      y={segment.labelY + (lineIndex - 0.5 + segment.multiLineLabel!.length / 2) * 18}
                      textAnchor={textAnchor}
                      dominantBaseline="middle"
                      className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
                      fill="#333333"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            } else {
              // Regular single-line label
              return (
                <text
                  key={`label-2024-${i}`}
                  x={segment.labelX}
                  y={segment.labelY}
                  textAnchor={textAnchor}
                  dominantBaseline="middle"
                  className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
                  fill="#333333"
                >
                  {segment.label}
                </text>
              );
            }
          })}

          {/* 2027 Pie segments */}
          {segments2027.map((segment, i) => (
            <path
              key={`segment-2027-${i}`}
              d={segment.path}
              fill={segment.fill}
              fillOpacity={segment.fill === "var(--accent)" ? segment.fillOpacity : 0.9}
              stroke="#ffffff"
              strokeWidth=".5"
            />
          ))}

          {/* 2027 Labels */}
          {segments2027.map((segment, i) => {
            // Determine text anchor based on position
            const textAnchor = Math.cos(segment.labelAngle) > 0 ? "start" : "end";

            // If it's a multi-line label
            if (segment.multiLineLabel && segment.multiLineLabel.length > 1) {
              return (
                <g key={`label-2027-${i}`}>
                  {segment.multiLineLabel.map((line, lineIndex) => (
                    <text
                      key={`label-2027-${i}-line-${lineIndex}`}
                      x={segment.labelX}
                      y={segment.labelY + (lineIndex - 0.5 + segment.multiLineLabel!.length / 2) * 18}
                      textAnchor={textAnchor}
                      dominantBaseline="middle"
                      className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
                      fill="#333333"
                    >
                      {line}
                    </text>
                  ))}
                </g>
              );
            } else {
              // Regular single-line label
              return (
                <text
                  key={`label-2027-${i}`}
                  x={segment.labelX}
                  y={segment.labelY}
                  textAnchor={textAnchor}
                  dominantBaseline="middle"
                  className="text-[16px] font-mono md:text-[14px] sm:text-[12px]"
                  fill="#333333"
                >
                  {segment.label}
                </text>
              );
            }
          })}
        </g>
      </svg>
    </div>
  );
}
